import { fabric } from "fabric";
import { v4 as uuid } from "uuid";

/**
 * Integration widget (Integrationer (beta)): one Fabric group per data source. It stores only
 * { integrationId, dataType, feedId, config, rows, bg, fg } — never keys. Screens refill the named
 * texts from /api/feeds/:feedId (public/integration-widgets.js, same layout rules as here).
 *
 * Children: int-bg, int-title, int-status, and per data type
 *   feed.items          int-row-{i}
 *   transit.departures  int-row-{i}, int-time-{i}
 *   weather.current     int-temp, int-desc
 */
export const INTEGRATION_ELE_TYPE = "integration-widget";
export const SUPPORTED_DATA_TYPES = ["feed.items", "weather.current", "transit.departures"];

const W = 640;
const PAD = 20;
const HEADER_H = 76;
const ROW_H = 52;
const FOOT_H = 34;

export function defaultIntegrationObj() {
  return { integrationId: "", dataType: "feed.items", feedId: "", config: {}, rows: 5, bg: "#0f172a", fg: "#ffffff" };
}

function rowsOf(o) {
  return Math.min(Math.max(parseInt(o.rows, 10) || 5, 1), 10);
}

export function integrationHeight(o) {
  if (o.dataType === "weather.current") return 230;
  return HEADER_H + rowsOf(o) * ROW_H + FOOT_H;
}

export function buildIntegrationObjects(obj) {
  const o = { ...defaultIntegrationObj(), ...(obj || {}) };
  const H = integrationHeight(o);
  const base = { selectable: false, evented: false, objectCaching: false, fontFamily: "Arial" };
  const objs = [
    new fabric.Rect({ ...base, name: "int-bg", left: 0, top: 0, width: W, height: H, fill: o.bg, rx: 18, ry: 18 }),
    new fabric.Text(o.feedId ? "…" : "Välj datakälla", {
      ...base, name: "int-title", left: PAD, top: 22, fontSize: 30, fontWeight: "bold", fill: o.fg,
    }),
    new fabric.Text("", {
      ...base, name: "int-status", left: W - PAD, top: H - 28, originX: "right", fontSize: 15, fill: o.fg, opacity: 0.6,
    }),
  ];
  if (o.dataType === "weather.current") {
    objs.push(
      new fabric.Text("", { ...base, name: "int-temp", left: PAD, top: 78, fontSize: 84, fontWeight: "bold", fill: o.fg }),
      new fabric.Text("", { ...base, name: "int-desc", left: PAD + 4, top: 178, fontSize: 26, fill: o.fg })
    );
  } else {
    for (let i = 0; i < rowsOf(o); i++) {
      const y = HEADER_H + i * ROW_H + ROW_H / 2;
      objs.push(new fabric.Text("", { ...base, name: `int-row-${i}`, left: PAD, top: y, originY: "center", fontSize: 24, fill: o.fg }));
      if (o.dataType === "transit.departures") {
        objs.push(new fabric.Text("", {
          ...base, name: `int-time-${i}`, left: W - PAD, top: y, originX: "right", originY: "center", fontSize: 24, fontWeight: "bold", fill: o.fg,
        }));
      }
    }
  }
  return objs;
}

export function createIntegrationGroup(obj, position = { left: 120, top: 120 }) {
  const data = { ...defaultIntegrationObj(), ...(obj || {}) };
  const group = new fabric.Group(buildIntegrationObjects(data), {
    left: position.left,
    top: position.top,
    scaleX: position.scaleX || 1,
    scaleY: position.scaleY || 1,
    subTargetCheck: false,
    hasBorders: true,
    hasControls: true,
    lockRotation: true,
  });
  group.id = position.id || uuid();
  group.eleType = INTEGRATION_ELE_TYPE;
  group.integrationObj = data;
  group.setCoords();
  return group;
}

function clip(s, n) {
  const t = String(s || "");
  return t.length > n ? `${t.slice(0, n - 1)}…` : t;
}

function hhmm(iso) {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "";
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function departureTime(dep, now) {
  if (dep.cancelled) return "Inställd";
  const t = Date.parse(dep.estimated || dep.planned || "");
  if (!Number.isFinite(t)) return "";
  const min = Math.floor((t - now) / 60000);
  if (min <= 0) return "Nu";
  if (min < 60) return `${min} min`;
  return hhmm(new Date(t).toISOString());
}

/** Fill a widget from a /api/feeds response (editor preview; mirrors public/integration-widgets.js). */
export function fillIntegrationGroup(group, payload, now = Date.now()) {
  const subs = group._objects || [];
  const byName = (n) => subs.find((c) => c.name === n);
  const set = (n, props) => byName(n) && byName(n).set(props);
  const o = group.integrationObj || defaultIntegrationObj();
  const data = payload && payload.data;
  if (!payload || payload.status === "disconnected" || !data) {
    set("int-title", { text: payload && payload.status === "disconnected" ? "Integrationen är frånkopplad" : "Ingen data än" });
    set("int-status", { text: "" });
    group.dirty = true;
    return;
  }
  set("int-status", { text: payload.updatedAt ? `${payload.stale ? "Senast hämtad" : "Uppdaterad"} ${hhmm(payload.updatedAt)}` : "" });
  if (o.dataType === "weather.current") {
    set("int-title", { text: clip(data.place || "Väder", 30) });
    set("int-temp", { text: Number.isFinite(data.temp) ? `${data.temp}°` : "–" });
    set("int-desc", { text: clip(data.description ? data.description[0].toUpperCase() + data.description.slice(1) : "", 40) });
  } else if (o.dataType === "transit.departures") {
    set("int-title", { text: clip(data.stopName || "Avgångar", 34) });
    const deps = (data.departures || []).filter((d) => {
      const t = Date.parse(d.estimated || d.planned || "");
      return !Number.isFinite(t) || t > now - 60000;
    });
    for (let i = 0; byName(`int-row-${i}`); i++) {
      const d = deps[i];
      set(`int-row-${i}`, { text: d ? clip(`${d.line}  ${d.direction}`, 38) : i === 0 ? "Inga avgångar" : "" });
      set(`int-time-${i}`, { text: d ? departureTime(d, now) : "", fill: d && d.cancelled ? "#f87171" : o.fg });
    }
  } else {
    set("int-title", { text: clip(data.title || "Nyheter", 34) });
    const items = data.items || [];
    for (let i = 0; byName(`int-row-${i}`); i++) {
      set(`int-row-${i}`, { text: items[i] ? clip(items[i].title, 48) : i === 0 ? "Inga rubriker" : "" });
    }
  }
  group.dirty = true;
}
